import { useEffect, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { Plus, Trash2, X, Save, FolderOpen, Upload, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", category: "smlouva", expiry_date: "", notes: "", client_id: "", job_id: "" };
const CATEGORIES = {
    smlouva: "Smlouva", certifikat: "Certifikát", revize: "Revize",
    pojistka: "Pojistka", bozp: "BOZP", jine: "Jiné",
};

export default function Documents() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [filter, setFilter] = useState("");

    const load = () => api.get("/documents").then((r) => setItems(r.data));
    const [jobs, setJobs] = useState([]);
    const [clients, setClients] = useState([]);
    useEffect(() => { load(); api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {}); api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

    const save = async () => {
        if (!editing.name) { toast.error("Zadejte název dokumentu"); return; }
        const payload = { name: editing.name, category: editing.category, expiry_date: editing.expiry_date || null, notes: editing.notes || "", client_id: editing.client_id || null, job_id: editing.job_id || null };
        try {
            if (editing.id) await api.put(`/documents/${editing.id}`, payload);
            else {
                const { data } = await api.post("/documents", payload);
                if (editing._file) await uploadFile(data.id, editing._file, false);
            }
            toast.success("Dokument uložen");
            setEditing(null);
            load();
        } catch { toast.error("Uložení selhalo"); }
    };

    const uploadFile = async (id, file, reload = true) => {
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/documents/${id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            if (reload) { toast.success("Soubor nahrán"); load(); }
        } catch { toast.error("Nahrání souboru selhalo"); }
    };

    const del = async (id) => {
        if (!window.confirm("Smazat dokument?")) return;
        await api.delete(`/documents/${id}`);
        load();
    };

    const filtered = items.filter((d) => !filter || d.category === filter);
    const expiring = items.filter((d) => d.days_to_expiry !== null && d.days_to_expiry >= 0 && d.days_to_expiry <= 30);

    return (
        <div className="px-10 py-10 max-w-6xl mx-auto animate-fade-in" data-testid="documents-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Kancelář</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Dokumenty firmy</h1>
                    <p className="text-sm text-slate-600 mt-2">Smlouvy, certifikáty, revize a pojistky — s hlídáním expirace.</p>
                </div>
                <button onClick={() => setEditing({ ...EMPTY })} data-testid="new-document-button"
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                    <Plus className="w-4 h-4" /> Nový dokument
                </button>
            </div>

            {expiring.length > 0 && (
                <div className="bg-amber-100 border-l-4 border-amber-600 p-4 mb-6 flex items-center gap-3 documents-expiry-banner" data-testid="documents-expiry-banner">
                    <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0" />
                    <div className="text-sm text-slate-900 font-medium">
                        <b>{expiring.length}</b> {expiring.length === 1 ? "dokument expiruje" : "dokumenty expirují"} do 30 dní: {expiring.map((d) => d.name).join(", ")}
                    </div>
                </div>
            )}

            <div className="flex gap-2 mb-4 flex-wrap">
                <button onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 border uppercase tracking-wider ${!filter ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>Vše</button>
                {Object.entries(CATEGORIES).map(([k, v]) => (
                    <button key={k} onClick={() => setFilter(filter === k ? "" : k)}
                            className={`text-xs px-3 py-1.5 border uppercase tracking-wider ${filter === k ? "bg-orange-600 text-white border-orange-600" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
                        {v}
                    </button>
                ))}
            </div>

            <div className="bg-white border border-slate-200">
                {filtered.length === 0 ? (
                    <div className="p-16 text-center">
                        <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
                        <div className="font-display text-lg font-bold text-slate-700">Žádné dokumenty</div>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b-2 border-slate-900">
                                <th className="px-6 py-3 font-semibold">Název</th>
                                <th className="px-6 py-3 font-semibold">Kategorie</th>
                                <th className="px-6 py-3 font-semibold">Platnost do</th>
                                <th className="px-6 py-3 font-semibold">Soubor</th>
                                <th className="px-6 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="tabular-data">
                            {filtered.map((d, i) => (
                                <tr key={d.id} className={`border-b border-slate-100 hover:bg-orange-50/40 ${i % 2 ? "bg-slate-50/40" : ""}`}>
                                    <td className="px-6 py-3">
                                        <button onClick={() => setEditing(d)} className="text-left font-medium text-slate-900 hover:text-orange-600">{d.name}</button>
                                        {d.notes && <div className="text-xs text-slate-500">{d.notes}</div>}
                                        {(d.client_name || d.job_title) && <div className="text-[11px] text-orange-700 mt-0.5" data-testid={`doc-links-${d.id}`}>{[d.client_name, d.job_title].filter(Boolean).join(" · ")}</div>}
                                    </td>
                                    <td className="px-6 py-3">
                                        <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-slate-300 bg-slate-50 text-slate-600">
                                            {CATEGORIES[d.category] || d.category}
                                        </span>
                                    </td>
                                    <td className="px-6 py-3">
                                        {d.expiry_date ? (
                                            <span className={d.days_to_expiry !== null && d.days_to_expiry <= 30 ? (d.days_to_expiry < 0 ? "text-red-700 font-bold" : "text-orange-700 font-bold") : "text-slate-600"}>
                                                {d.expiry_date}
                                                {d.days_to_expiry !== null && d.days_to_expiry <= 30 && (
                                                    <span className="block text-[10px] uppercase">{d.days_to_expiry < 0 ? "Expirováno!" : `za ${d.days_to_expiry} dní`}</span>
                                                )}
                                            </span>
                                        ) : "—"}
                                    </td>
                                    <td className="px-6 py-3">
                                        {d.file_path ? (
                                            <a href={fileUrl(d.file_path)} target="_blank" rel="noreferrer"
                                               className="inline-flex items-center gap-1 text-xs text-orange-700 hover:underline">
                                                <Download className="w-3 h-3" /> {d.filename || "Stáhnout"}
                                            </a>
                                        ) : (
                                            <label className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-orange-600 cursor-pointer">
                                                <Upload className="w-3 h-3" /> Nahrát
                                                <input type="file" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(d.id, e.target.files[0])} />
                                            </label>
                                        )}
                                    </td>
                                    <td className="px-6 py-3 text-right">
                                        <button onClick={() => del(d.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
                    <div className="bg-white border-2 border-slate-900 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                            <h2 className="font-display text-lg font-bold">{editing.id ? "Upravit dokument" : "Nový dokument"}</h2>
                            <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-slate-400 hover:text-slate-900" /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="label-eyebrow block mb-1.5">Název *</label>
                                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="doc-name"
                                       className="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label-eyebrow block mb-1.5">Kategorie</label>
                                    <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="doc-category"
                                            className="w-full px-3 py-2 border border-slate-300 text-sm rounded-sm bg-white focus:outline-none focus:border-orange-600">
                                        {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="label-eyebrow block mb-1.5">Platnost do</label>
                                    <input type="date" value={editing.expiry_date || ""} onChange={(e) => setEditing({ ...editing, expiry_date: e.target.value })} data-testid="doc-expiry"
                                           className="w-full px-3 py-2 border border-slate-300 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label-eyebrow block mb-1.5">Klient</label>
                                    <select value={editing.client_id || ""} onChange={(e) => setEditing({ ...editing, client_id: e.target.value })} data-testid="doc-client" className="w-full px-3 py-2 border border-slate-300 text-sm rounded-sm bg-white">
                                        <option value="">— bez klienta —</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="label-eyebrow block mb-1.5">Zakázka</label>
                                    <select value={editing.job_id || ""} onChange={(e) => { const j = jobs.find((x) => x.id === e.target.value); setEditing({ ...editing, job_id: e.target.value, client_id: editing.client_id || j?.client_id || "" }); }} data-testid="doc-job" className="w-full px-3 py-2 border border-slate-300 text-sm rounded-sm bg-white">
                                        <option value="">— bez zakázky —</option>{jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="label-eyebrow block mb-1.5">Poznámka</label>
                                <input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                                       className="w-full px-3 py-2 border border-slate-300 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                            </div>
                            {!editing.id && (
                                <div>
                                    <label className="label-eyebrow block mb-1.5">Soubor (volitelně)</label>
                                    <input type="file" onChange={(e) => setEditing({ ...editing, _file: e.target.files?.[0] })}
                                           className="w-full text-sm text-slate-600" />
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2 px-6 py-4 bg-slate-50 border-t border-slate-200 justify-end">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">Zrušit</button>
                            <button onClick={save} data-testid="save-document" className="px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm inline-flex items-center gap-1">
                                <Save className="w-3.5 h-3.5" /> Uložit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
